import { Loader } from "@googlemaps/js-api-loader";

// ITAM-2583 (phase-2): single-instance loader for the Google Maps JS Places
// Library (New). The whole app must share ONE bootstrap — the Maps JS runtime
// refuses a second inline loader — so the importLibrary("places") promise is
// cached in a module-level variable and every caller awaits the same instance.
//
// The API key is read from process.env at load time (CRA inlines REACT_APP_*
// vars at build). We NEVER hardcode, log, or read .env files here. The Places
// library is loaded OUTSIDE the app axios instance (it is Google's own network
// stack + its own session-token billing), so it never touches our bearer/logout
// interceptor.

const API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

let placesLibraryPromise: Promise<google.maps.PlacesLibrary> | null = null;

/**
 * Loads (once) and returns the Google Maps Places library. A SUCCESSFUL load is
 * cached so repeated callers share one bootstrap. A FAILED load clears the cache
 * so the AddressCreate "Pokušaj ponovo" (retry) button can re-bootstrap — without
 * this, the rejected promise stays cached forever and every retry re-rejects.
 *
 * Throws a clear error when the key env var is missing so the caller can render a
 * "cannot load address search" state rather than letting the SDK fail opaquely.
 */
export const loadPlacesLibrary = (): Promise<google.maps.PlacesLibrary> => {
  if (!API_KEY) {
    return Promise.reject(
      new Error(
        "REACT_APP_GOOGLE_PLACES_API_KEY is not set — the address search cannot be loaded."
      )
    );
  }

  if (!placesLibraryPromise) {
    const loader = new Loader({
      apiKey: API_KEY,
      version: "weekly",
      libraries: ["places"],
    });
    placesLibraryPromise = loader.importLibrary("places").catch((error) => {
      // Drop the cached rejection so a later call re-attempts the load.
      placesLibraryPromise = null;
      throw error;
    });
  }

  return placesLibraryPromise;
};
